// 261005 E1-P3a — hàm THUẦN của bảng Nhân vật/Bối cảnh chung 2 tầng (chặng ②). KHÔNG import antd / react
// (jest của react-scripts không load được antd v6). Component `EntityTable` chỉ vẽ lại kết quả.
import type { LocalizeEntityRow, LocalizeMeta, LocalizeView, OutlineCharacter, OutlineLocation } from '../../../types/adaptation';
import type { DangerCopyView, WorkbenchTarget } from './workbenchView';

export type EntityKind = 'character' | 'location';
export type EntityFilter = 'all' | 'missing';

export interface EntityRow {
  id: string;
  kind: EntityKind;
  name: string;
  /** Chữ mô tả vai (chỉ đọc) — chỉ nhân vật. */
  role?: string;
  tier?: OutlineCharacter['tier'];
  primary?: boolean;
  reusePlan?: string;
  from: string[];
  aliases: string[];
  localizedName: string;
  localizeMeta: LocalizeMeta | null;
  sourceAliases: string[];
  /** `false` khi view (tên) cũ hơn outline: chưa có dòng này trong `GET localize`. */
  nameKnown: boolean;
  /** Đã bản địa hoá mà dòng này chưa có tên. */
  missingName: boolean;
}

/** Đã bản địa hoá = có ít nhất một NHÂN VẬT mang tên (khớp `localizeActive` backend; bối cảnh không tính). */
export const localizeHasRun = (view: Pick<LocalizeView, 'characters'> | null | undefined): boolean =>
  (view?.characters ?? []).some((c) => !!c.localizedName?.trim());

interface OutlineLike { characters?: OutlineCharacter[]; locations?: OutlineLocation[] }

/** Thứ tự theo outline (nguồn sự thật về tồn tại). Dòng chỉ có trong view ⇒ bỏ. `view` null ⇒ tên rỗng. */
export const buildEntityRows = (
  kind: EntityKind, outline: OutlineLike | null | undefined, view: LocalizeView | null | undefined,
): EntityRow[] => {
  const hasRun = localizeHasRun(view);
  const viewList: LocalizeEntityRow[] = (kind === 'character' ? view?.characters : view?.locations) ?? [];
  const byId = new Map(viewList.map((r) => [r.id, r]));
  const list = (kind === 'character' ? outline?.characters : outline?.locations) ?? [];
  return list.map((o: any) => {
    const v = byId.get(o.id);
    const localizedName = (v?.localizedName ?? '').toString();
    return {
      id: o.id, kind, name: o.name,
      role: kind === 'character' ? o.role : undefined,
      tier: kind === 'character' ? o.tier : undefined,
      primary: kind === 'location' ? !!o.primary : undefined,
      reusePlan: kind === 'location' ? o.reusePlan : undefined,
      from: o.from ?? [], aliases: o.aliases ?? [],
      localizedName,
      localizeMeta: v?.localizeMeta ?? null,
      sourceAliases: v?.sourceAliases ?? o.sourceAliases ?? [],
      nameKnown: !!v,
      missingName: hasRun && !localizedName.trim(),
    };
  });
};

// ─── Trạng thái từng ô ───────────────────────────────────────────────────

export interface CellCtx {
  /** Chặng ② đang khoá (lý do hiện ở Alert của StageFrame). */
  stageLocked: boolean;
  /** `ctx.busy` của vỏ HOẶC `working` dùng chung. */
  busy: boolean;
  /** Khung xương đã duyệt. */
  approved: boolean;
  hasRun: boolean;
  viewLoaded: boolean;
}
export interface CellState { enabled: boolean; reason?: string }
export interface EntityCellState { structure: CellState; name: CellState & { placeholder: string }; keep: CellState }

const BUSY = 'Đang có tác vụ chạy';
const LOCKED = 'Bước đang khoá';

export const entityCellState = (row: EntityRow, ctx: CellCtx): EntityCellState => {
  const base = (): CellState | null => {
    if (ctx.stageLocked) return { enabled: false, reason: LOCKED };
    if (ctx.busy) return { enabled: false, reason: BUSY };
    return null;
  };
  const b = base();
  const structure: CellState = b ?? { enabled: true };

  const locked = !!row.localizeMeta?.locked;
  let name: EntityCellState['name'] = { enabled: true, placeholder: '' };
  if (b) name = { ...b, placeholder: '' };
  else if (!ctx.viewLoaded) name = { enabled: false, reason: 'Chưa tải được tên', placeholder: '' };
  else if (!ctx.hasRun) name = { enabled: false, reason: 'Chạy bản địa hoá ở ③ trước', placeholder: '—' };
  else if (locked) name = { enabled: false, reason: 'Đang giữ tên — tắt Giữ tên để sửa', placeholder: '' };

  let keep: CellState = { enabled: true };
  if (b) keep = b;
  else if (!ctx.viewLoaded) keep = { enabled: false, reason: 'Chưa tải được tên' };
  else if (!ctx.hasRun) keep = { enabled: false, reason: 'Chạy bản địa hoá ở ③ trước' };
  else if (!row.localizedName.trim() && !locked) keep = { enabled: false, reason: 'Chỉ giữ được tên trong phim đã có' };

  return { structure, name, keep };
};

// ─── Hộp hỏi lại khi sửa khung xương ──────────────────────────────────────

export type StructureAction = 'setTier' | 'setPrimary' | 'merge' | 'remove';
export interface StructureCtx {
  approved: boolean; name: string;
  /** Tên trong phim của dòng BỊ gộp (chỉ `merge`). */
  dropLocalizedName?: string;
  /** Nhãn mới của hạng/loại (vd "Phụ"). */
  newLabel?: string;
  /** `setPrimary` ⇒ "loại" thay "hạng" (bối cảnh). */
  isLocation?: boolean;
}

export const structureConfirm = (action: StructureAction, c: StructureCtx): DangerCopyView | null => {
  const draftLine = c.approved ? 'Khung xương đã duyệt sẽ về bản nháp.' : 'Khung xương vẫn là bản nháp.';
  switch (action) {
    case 'setTier': case 'setPrimary': {
      if (!c.approved) return null;
      const what = action === 'setPrimary' || c.isLocation ? 'loại' : 'hạng';
      return {
        title: `Đổi ${what} ${c.name}${c.newLabel ? ` thành ${c.newLabel}` : ''}?`, danger: false, okText: 'Đổi',
        content: 'Khung xương đã duyệt sẽ về bản nháp. Chia tập và kịch bản đã có được giữ nguyên, nhưng phải duyệt lại mới chia tập / viết tiếp.',
      };
    }
    case 'merge': {
      const lost = c.dropLocalizedName?.trim()
        ? `Tên trong phim “${c.dropLocalizedName.trim()}” của ${c.name}, bí danh nguyên tác và cặp đổi tên của ${c.name} sẽ mất.`
        : `Tên trong phim, bí danh nguyên tác và cặp đổi tên của ${c.name} sẽ mất.`;
      return {
        title: `Gộp ${c.name}?`, danger: true, okText: 'Gộp',
        content: `Khôi phục được ở mục Phiên bản của Trợ lý AI (bước ②). Mọi chỗ có ${c.name} sẽ thuộc về nhân vật được giữ. ${lost} ${draftLine}`,
      };
    }
    case 'remove': default:
      return {
        title: `Bỏ ${c.name} khỏi khung xương?`, danger: true, okText: 'Bỏ',
        content: `Kịch bản đã viết không bị sửa. ${draftLine}`,
      };
  }
};

// ─── Lọc · đếm · dòng trạng thái ──────────────────────────────────────────

export const entityFilter = (rows: EntityRow[], filter: EntityFilter): { rows: EntityRow[]; missing: number } => {
  const missing = rows.filter((r) => r.missingName).length;
  return { rows: filter === 'missing' ? rows.filter((r) => r.missingName) : rows, missing };
};

/** Bộ lọc thực dùng: `missing` chỉ hợp lệ khi đã chạy và còn dòng thiếu; hết thiếu ⇒ tự về `all`. */
export const effectiveFilter = (filter: EntityFilter, hasRun: boolean, missing: number): EntityFilter =>
  filter === 'missing' && hasRun && missing > 0 ? 'missing' : 'all';

export interface StatusLine { tone: 'info' | 'warning'; text: string }
export const localizeStatusLine = (
  p: { localizeEnabled: boolean; hasRun: boolean; resultStale?: boolean },
): StatusLine | null => {
  if (!p.localizeEnabled) {
    return { tone: 'info', text: 'Truyện này tắt bản địa hoá: không chạy được lượt mới. Tên trong phim đã đặt (nếu có) vẫn được dùng khi viết kịch bản; chưa đặt thì dùng tên gốc.' };
  }
  if (!p.hasRun) return { tone: 'info', text: 'Chưa bản địa hoá — tên trong phim sẽ có sau khi chạy ở ③.' };
  if (p.resultStale) return { tone: 'warning', text: 'Chính sách bản địa hoá đã đổi — tên hiện tại thuộc chính sách cũ (làm lại ở ③).' };
  return null;
};

export interface LocalizeNameCounts {
  namedChars: number; totalChars: number; namedLocs: number; totalLocs: number; locked: number;
  missingChars: Array<{ id: string; name: string }>;
}
/** Số liệu cho thẻ "Tên trong phim" ở ③. */
export const localizeNameCounts = (view: LocalizeView | null | undefined): LocalizeNameCounts => {
  const chars = view?.characters ?? [];
  const locs = view?.locations ?? [];
  const has = (e: LocalizeEntityRow) => !!e.localizedName?.trim();
  return {
    namedChars: chars.filter(has).length, totalChars: chars.length,
    namedLocs: locs.filter(has).length, totalLocs: locs.length,
    locked: [...chars, ...locs].filter((e) => e.localizeMeta?.locked).length,
    missingChars: chars.filter((c) => !has(c)).map((c) => ({ id: c.id, name: c.name })),
  };
};

export const entityFocusTarget = (kind?: EntityKind, id?: string, filter?: 'missing'): WorkbenchTarget => {
  const t: WorkbenchTarget = { stage: 'skeleton', intent: 'focusEntities' };
  if (kind) t.entityKind = kind;
  if (id) t.entityId = id;
  if (filter) t.entityFilter = filter;
  return t;
};

const LOCATION_TIER_LABEL: Record<string, string> = { city: 'Thành phố', region: 'Vùng', venue: 'Địa điểm' };
/** Nhãn cấp bối cảnh (`localizeMeta.tier`); mã lạ ⇒ hiện nguyên. */
export const locationTierLabel = (tier: string): string => LOCATION_TIER_LABEL[tier] ?? tier;
